<template>
  <div>
    <div class="container">
      <div class="headline">
        <h1
          class="h1--underline"
        >
          {{ $tv('headline', 'guidePage') }}
        </h1>
      </div>
      <GuideList
        class="card__container container--guide"
        :guide-list="guides"
        :category="category || undefined"
      />
    </div>
    <NuxtLink
      :to="localePath('/guides')"
      class="k-button k-button-md k-button-solid k-button-solid-base button__category"
    >
      <NavigationChevronLeft class="svg-navigation-chevron-left" />
      <span class="page__button--content">{{ $tv('go-back-button', 'guide') }}</span>
    </NuxtLink>
  </div>
</template>
<script setup lang="ts">
import GuideList from '~/components/guide/GuideList.vue'
import {useGuideService} from '~/composables/guide_service'
import NavigationChevronLeft from '~/assets/icons/navigation_chevron_left.svg?component'
import useBreadcrumb from '~/composables/useBreadcrumb'
import useI18nExtended from '~/composables/usei18nExtended'

const i18n = useI18nExtended()
const route = useRoute()
const params = route?.params
const {getGuidesByCategory, getGuideCategoryById, getGuideCategoryByUrlWithLocalizations} = useGuideService()
const nuxtApp = useNuxtApp()
const guides = ref([])
const category = ref(null)

// Function to set up translated route params for locale switching
const setupTranslatedParams = async (categoryData) => {
  if (!categoryData?.localizations || categoryData.localizations.length === 0) {return}

  let translatedParams = {}

  // Fetch each localization to get the Url
  const localizationPromises = categoryData.localizations.map(async (loc) => {
    if (loc.locale !== i18n.locale.value) {
      const localizedData = await getGuideCategoryById(loc.id, loc.locale)
      if (localizedData?.Url) {
        translatedParams[loc.locale] = {guideCategories: localizedData.Url}
      }
    }
  })

  await Promise.all(localizationPromises)

  if (Object.entries(translatedParams).length > 0) {
    route.meta.nuxtI18n = translatedParams
  }
}

let guideData = nuxtApp.payload?.data[`guides_${i18n.locale.value}_0_-1_null_null_{"field":"guide_categories.Url","operator":"eq","value":"${params.guideCategories}"}_false`]

// Always fetch category with localizations for proper URL translation on locale switch
let categoryData = await getGuideCategoryByUrlWithLocalizations(params.guideCategories, i18n.locale.value)

if (!guideData) {
  guideData = await getGuidesByCategory(params.guideCategories, i18n.locale.value)
}
if (guideData && categoryData) {
  guides.value =  guideData
  category.value = categoryData
  // Set up translated params for locale switching in URL
  await setupTranslatedParams(categoryData)
} else {
  throw showError({statusCode: 404, statusMessage: 'Page not found for Guide Category: ' + params.guideCategories})
}

watch(() => i18n.locale.value, async (newLocale, oldLocale) => {
  if (!newLocale || !oldLocale) {return}

  // Find the localized category ID from localizations
  const localizedCategory = category.value?.localizations?.find(l => l.locale === newLocale)

  if (!localizedCategory?.id) {
    // eslint-disable-next-line no-console
    console.warn(`No category localization found for locale "${newLocale}"`)
    return
  }

  // Fetch the category by ID to get its Url
  const categoryById = await getGuideCategoryById(localizedCategory.id, newLocale)

  if (categoryById?.Url) {
    // Fetch again with localizations for future locale switches
    const newCategoryData = await getGuideCategoryByUrlWithLocalizations(categoryById.Url, newLocale)
    const newGuideData = await getGuidesByCategory(categoryById.Url, newLocale)

    if (newGuideData) {
      guides.value = newGuideData
    }
    category.value = newCategoryData || categoryById
    // Update translated params for next locale switch
    await setupTranslatedParams(category.value)
  } else {
    // eslint-disable-next-line no-console
    console.warn(`Could not fetch category for locale "${newLocale}"`)
  }
})
const breadcrumbs = computed(() => [
  {
    label: i18n.te('breadcrumbs.guides') ? i18n.tv('guides', 'breadcrumbs') : i18n.options.name,
    path: '/guides'
  },
  {
    label: i18n.tv(category.value?.Title, 'breadcrumbs'),
    path: `/guides/${encodeURIComponent(category.value?.Url)}`
  }
])
useBreadcrumb(breadcrumbs.value)
watch(breadcrumbs, (newBreadcrumbs) => {
  useBreadcrumb(newBreadcrumbs)
})

useHead({
  meta: [{hid: 'robots', name: 'robots', content: 'noindex'}]
})

</script>

<style lang="scss">
.headline {
  padding-left: 2rem;
  padding-bottom: 1rem;
  background-color: $color__fst__white;
}

.button__category {
  margin-top: $default-margin-padding-2x;
  margin-bottom: $default-margin-padding-width;
  margin-left: $default-margin-padding-2x;

  .svg-navigation-chevron-left {
    font-size: 1.5rem;
    margin-left: -.3em;
    margin-right: .5em;
  }
}
</style>
